import React from 'react'
import PropTypes from 'prop-types'
import { Stack, Tooltip } from '@mui/material'
import PauseIcon from '@mui/icons-material/Pause'
import CheckIcon from '@mui/icons-material/Check'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { useSmartTranslation } from '../../../../../../generic/hooks/useSmartTranslation'
import WorkerRequiredButton from '../../../../common/WorkerRequiredButton'
import { JOB_ACTION_INTENT } from '../../utils/jobStatus'
import { resolveDetailActions } from '../../utils/jobDetailActions'
import { SINGLE_JOB_MODE_BLOCKED_BUTTON_SX } from '../../../../common/canStartJob'

// Historia 160 (CU10, RN20-RN22, mock 1) — las acciones de estado de la cabecera, arriba a la
// derecha (D8). La regla es `resolveDetailActions` (una sola, D13); los rótulos son las MISMAS claves
// que la fila ("Start", "Finish", "Interrupt", "Join"), aunque el mock escriba "Finalizar" (S69).
//
// Sin operario, `WorkerRequiredButton` pide uno y no hace nada más (RN21/S61), igual que en la fila.

const ICON_BY_INTENT = {
  [JOB_ACTION_INTENT.PAUSE]: <PauseIcon />,
  [JOB_ACTION_INTENT.FINISH]: <CheckIcon />,
  [JOB_ACTION_INTENT.START]: <PlayArrowIcon />
}

// Mock 1: la principal va a la derecha y rellena ("Interrumpir" contorno, "Finalizar" relleno).
// Arrancar (o reanudar) manda sobre finalizar, y finalizar sobre interrumpir.
const PRIMARY_RANK = { [JOB_ACTION_INTENT.PAUSE]: 0, [JOB_ACTION_INTENT.FINISH]: 1, [JOB_ACTION_INTENT.START]: 2 }
const byRank = (left, right) => (PRIMARY_RANK[left.intent] || 0) - (PRIMARY_RANK[right.intent] || 0)

const BUTTON_SX = { minHeight: 48, px: 2.5, fontWeight: 700, textTransform: 'none', fontSize: '1.05rem' }

/**
 * @param {Object} props
 * @param {Object} props.job `job` de D1
 * @param {boolean} props.blocked incidencia de puesto que bloquea producción (misma condición que la fila)
 * @param {string} props.startBlockedTooltipKey clave i18n cuando Single Job Mode retiene el Start/Join
 *   de este job (otro job activo en la máquina); '' cuando no bloquea. Misma regla que la fila
 *   (`isBlockedBySingleJobMode`). El Start/Join retenido conserva su rótulo y su sitio, PARECE
 *   deshabilitado, lleva el tooltip y al pulsarlo llama a `onStartBlocked` en vez de arrancar nada;
 *   Finish/Interrupt no se tocan.
 * @param {Function} props.onStartBlocked () => void, al pulsar un Start/Join retenido
 * @param {Function} props.onAction (action) => void, con `{ label, statusCode, intent }`
 */
const JobDetailStatusActions = ({ job = {}, blocked = false, startBlockedTooltipKey = '', onStartBlocked = () => {}, onAction = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const { actions, gated, tooltipKey } = resolveDetailActions(job)
  const startBlocked = !gated && startBlockedTooltipKey !== ''

  if (actions.length === 0) {
    // RN20.1/RN20.4: sin acciones, un Start deshabilitado; con gating, con el tooltip de la fila.
    const disabledStart = (
      <WorkerRequiredButton variant='outlined' disabled startIcon={<PlayArrowIcon />} sx={BUTTON_SX} onClick={() => {}}>
        {t_('Start')}
      </WorkerRequiredButton>
    )
    return gated
      ? <Tooltip title={t_(tooltipKey)}><span data-testid='gated-start'>{disabledStart}</span></Tooltip>
      : disabledStart
  }

  return (
    <Stack direction='row' spacing={1.5} data-testid='job-detail-actions'>
      {[...actions].sort(byRank).map((action, index) => {
        const primary = index === actions.length - 1
        const withheld = startBlocked && action.intent === JOB_ACTION_INTENT.START
        const button = (
          <WorkerRequiredButton
            key={action.label}
            variant={primary ? 'contained' : 'outlined'}
            color={primary ? 'primary' : 'inherit'}
            disabled={blocked}
            startIcon={ICON_BY_INTENT[action.intent]}
            sx={withheld ? { ...BUTTON_SX, ...SINGLE_JOB_MODE_BLOCKED_BUTTON_SX } : BUTTON_SX}
            onClick={withheld ? () => onStartBlocked() : () => onAction(action)}
          >
            {t_(action.label)}
          </WorkerRequiredButton>
        )
        return withheld
          ? <Tooltip key={action.label} title={t_(startBlockedTooltipKey)}><span data-testid='single-job-blocked-start'>{button}</span></Tooltip>
          : button
      })}
    </Stack>
  )
}

JobDetailStatusActions.propTypes = {
  job: PropTypes.object,
  blocked: PropTypes.bool,
  startBlockedTooltipKey: PropTypes.string,
  onStartBlocked: PropTypes.func,
  onAction: PropTypes.func
}

export default JobDetailStatusActions
